import React, { useState } from 'react';
import './ModalPageTwo.css';
import FileUpload from './FileUpload';

function ModalPageTwo(props) {
  // No local state for name/email - use parent state directly
  // This eliminates sync issues that were causing values to be overwritten
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get name/email directly from props for validation and display
  const currentName = props.formData.name || '';
  const currentEmail = props.formData.email || '';
  const handleSubmit = e => {
    e.preventDefault();

    // Validate required fields
    // Use local state for name/email since those are what the user sees in the form
    const {
      phoneNumber,
      deliveryDate,
      isStudent,
      school,
    } = props.formData;

    // Check basic required fields
    if (!currentName || !currentEmail || !phoneNumber) {
      alert(
        'Please fill in all required fields: Name, Email, and Phone Number'
      );
      return;
    }

    // Check delivery date for students
    if (isStudent && !deliveryDate) {
      alert('Please select a delivery date');
      return;
    }

    // Check school for students
    if (isStudent && !school) {
      alert('Please select your school');
      return;
    }

    // Check business name for non-students
    const businessName = props.formData.businessName;
    if (!isStudent && !businessName) {
      alert('Please enter your business or organization name');
      return;
    }

    // Basic email validation
    const emailRegex = /.+@.+\..+/;
    if (!emailRegex.test(currentEmail)) {
      alert('Please enter a valid email address');
      return;
    }

    // Set submitting state and call parent handler
    setIsSubmitting(true);
    props.handleSubmitQuoteRequest().catch(function() {
      // Reset submitting state on error so user can try again
      setIsSubmitting(false);
    });
  };

  const handleBack = e => {
    e.preventDefault();
    props.handleBack();
  };

  const isOwnArtwork = props.formData.orderType === 'own_artwork';
  const isContactOnly = props.formData.orderType === 'contact_only';

  const handleUploadedArtworkChange = uploadedArtwork => {
    props.handleChange('uploadedArtwork', uploadedArtwork);
  };

  // Determine header text based on order type
  const getHeaderText = () => {
    if (isOwnArtwork) {
      return {
        title: 'Upload Your Artwork',
        subtitle:
          "Upload your design files and share your details. We'll reach out shortly.",
      };
    }
    if (isContactOnly) {
      return {
        title: 'Tell Us About Your Project',
        subtitle:
          "Share your details and we'll reach out to help you get started.",
      };
    }
    return {
      title: 'Give us the deets',
      subtitle: 'Once you submit your design, we will contact you shortly.',
    };
  };

  const headerText = getHeaderText();

  const pageTwoHeader = (
    <div>
      <p className="uppercase centered mtn">2 of 2</p>
      <h2 className="heading-cofo c-heading c-heading-2 centered mvn">
        {headerText.title}
      </h2>
      <p className="mts mbl centered small">{headerText.subtitle}</p>
    </div>
  );

  const studentCheckboxUI = (
    <div className="col-4--m row row-gap-small mbm pas">
      <input
        type="checkbox"
        id="student-checkbox"
        checked={!props.formData.isStudent || false}
        onChange={e => {
          props.handleChange('isStudent', !e.target.checked);
        }}
      />
      <label htmlFor="student-checkbox" className="strong">
        Not a student?
      </label>
    </div>
  );

  function formatPhoneNumber(input) {
    // Remove all non-numeric characters
    input = input.replace(/\D/g, '');

    // Limit to 10 digits max
    input = input.slice(0, 10);

    // Format the input as xxx-xxx-xxxx
    if (input.length > 6) {
      input = input.slice(0, 3) + '-' + input.slice(3, 6) + '-' + input.slice(6);
    } else if (input.length > 3) {
      input = input.slice(0, 3) + '-' + input.slice(3);
    }

    return input;
  }

  const phoneFieldUI = (
    <div className="stack stack--small">
      <label className="input-label-thin" htmlFor="phone">
        Phone Number*
      </label>
      <input
        className="field"
        type="tel"
        id="phone"
        required={true}
        placeholder="Ex: 555-555-5555"
        value={props.formData.phoneNumber || ''}
        onChange={e => {
          const formattedPhoneNumber = formatPhoneNumber(e.target.value);
          props.handleChange('phoneNumber', formattedPhoneNumber);
        }}
      />
    </div>
  );

  const schoolFieldUI = (
    <div className="stack stack--small">
      <label className="input-label-thin" htmlFor="school">
        School*
      </label>
      <select
        className="field"
        type="text"
        id="school"
        required={true}
        value={props.formData.school}
        onChange={e => {
          props.handleChange('school', e.target.value);
        }}
        onBlur={e => {
          props.handleChange('school', e.target.value);
        }}
      >
        {props.schools.map(school => (
          <option key={school} value={school}>
            {school}
          </option>
        ))}
      </select>
    </div>
  );

  const organizationFieldUI = (
    <div className="stack stack--small">
      <label className="input-label-thin" htmlFor="organization">
        Organization
      </label>
      <input
        className="field"
        type="text"
        id="organization"
        value={props.formData.organization || ''}
        placeholder="Ex: Chi Omega, Nursing"
        onChange={e => {
          props.handleChange('organization', e.target.value);
        }}
      />
    </div>
  );

  const quantityFieldUI = (
    <div className="stack stack--small">
      <label className="input-label-thin" htmlFor="quantity">
        Estimated Quantity
      </label>
      <input
        className="field"
        type="text"
        id="quantity"
        value={props.formData.quantity || ''}
        placeholder="Ex: 100"
        onChange={e => {
          props.handleChange('quantity', e.target.value);
        }}
      />
    </div>
  );

  const deliveryDateUI = (
    <div className="stack stack--small">
      <label className="input-label-thin" htmlFor="delivery-date">
        Delivery Date*
      </label>
      <input
        className="field field--date"
        type="date"
        id="delivery-date"
        placeholder="MM/DD/YYYY"
        value={props.formData.deliveryDate || ''}
        required={true}
        onChange={e => {
          props.handleChange('deliveryDate', e.target.value);
        }}
      />
    </div>
  );

  const budgetFieldUI = (
    <div className="stack stack--small">
      <label className="input-label-thin" htmlFor="budget">
        Budget
      </label>
      <input
        className="field"
        type="text"
        id="budget"
        value={props.formData.budget || ''}
        placeholder="Ex: $1250"
        onChange={e => {
          props.handleChange('budget', e.target.value);
        }}
      />
    </div>
  );

  const businessNameFieldUI = (
    <div className="stack stack--small">
      <label className="input-label-thin" htmlFor="business">
        Business / Organization*
      </label>
      <input
        className="field"
        type="text"
        id="business"
        required={true}
        value={props.formData.businessName || ''}
        placeholder="Ex: Apple, Inc."
        onChange={e => {
          props.handleChange('businessName', e.target.value);
        }}
      />
    </div>
  );

  const eventName = (
    <div className="stack stack--small">
      <label className="input-label-thin" htmlFor="eventName">
        Event Name
      </label>
      <input
        className="field"
        id="eventName"
        value={props.formData.eventName || ''}
        placeholder="Ex. Bid Day"
        onChange={e => {
          props.handleChange('eventName', e.target.value);
        }}
      />
    </div>
  );

  const nameUI = (
    <div className="stack stack--small">
      <label className="input-label-thin" htmlFor="name">
        Name*
      </label>
      <input
        className="field"
        type="text"
        id="name"
        value={currentName}
        required={true}
        placeholder="Ex: Johnny Appleseed"
        onChange={e => {
          props.handleChange('name', e.target.value);
        }}
      />
    </div>
  );

  const emailUI = (
    <div className="stack stack--small">
      <label className="input-label-thin" htmlFor="email">
        Email*
      </label>
      <input
        className="field"
        type="email"
        id="email"
        required={true}
        placeholder="Ex: email@address.com"
        value={currentEmail}
        onChange={e => {
          props.handleChange('email', e.target.value);
        }}
      />
    </div>
  );

  // Determine back button text based on order type
  const getBackButtonText = () => {
    if (isOwnArtwork || isContactOnly) {
      return 'Back';
    }
    return 'Back to Customizing';
  };

  // Determine submit button text based on order type
  const getSubmitButtonText = () => {
    if (isSubmitting) {
      return 'Submitting...';
    }
    if (isOwnArtwork) {
      return 'Submit Request';
    }
    if (isContactOnly) {
      return 'Get in Touch';
    }
    return 'Submit Design';
  };

  const buttonUI = (
    <div className="row row-pushed">
      <div>
        <button className="button button--alt" onClick={handleBack}>
          {getBackButtonText()}
        </button>
      </div>
      <div>
        <button
          type="submit"
          form="quote-request-details"
          className="button"
          disabled={isSubmitting}
        >
          {getSubmitButtonText()}
        </button>
      </div>
    </div>
  );

  const orderNotesUI = (
    <div className="mtn pas">
      <label className="input-label-thin" htmlFor="orderNotes">
        Anything else?
      </label>
      <textarea
        id="orderNotes"
        value={props.formData.orderNotes || ''}
        className="field"
        onChange={e => {
          props.handleChange('orderNotes', e.target.value);
        }}
        placeholder="Provide any additional information that will help with your order request."
      />
    </div>
  );

  const salesStreamSpecificQuoteFields = props.formData.isStudent ? (
    <>
      {schoolFieldUI}
      {organizationFieldUI}
      {eventName}
    </>
  ) : (
    <>{businessNameFieldUI}</>
  );

  const fileUploadUI = isOwnArtwork ? (
    <div className="pas">
      <FileUpload
        uploadedArtwork={props.formData.uploadedArtwork}
        onUploadedArtworkChange={handleUploadedArtworkChange}
      />
    </div>
  ) : null;

  const quoteFormUI = (
    <form onSubmit={handleSubmit} id="quote-request-details">
      {fileUploadUI}
      {isOwnArtwork && <hr />}
      <div className="grid-2_1 pas">
        {nameUI}
        {emailUI}
        {phoneFieldUI}
        {deliveryDateUI}
        {budgetFieldUI}
        {quantityFieldUI}
      </div>
      <hr></hr>
      {studentCheckboxUI}
      <div className="grid-2_1 pas">{salesStreamSpecificQuoteFields}</div>
      {orderNotesUI}
    </form>
  );

  return (
    <div className="modal-page-two">
      <div className="modal-page-two-content">
        {pageTwoHeader}
        {quoteFormUI}
      </div>
      <div className="modal-page-two-footer">{buttonUI}</div>
    </div>
  );
}
export default ModalPageTwo;
